import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { ICreateDto } from "@/lib/utility/data/create-dto.interface";
import leadGroupService from "@/services/mrfiktiv/services/lead-group.service";
import {
  MrfiktivCreateLeadGroupDtoGen,
  MrfiktivLeadGroupViewModelGen,
  MrfiktivUpdateLeadGroupDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { LeadGroupDataAccessLayer } from "@/store/modules/access-layers/lead-group.access-layer";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import Vue from "vue";
import { IProjectConfiguration, ProjectConfiguration } from "./project-configuration.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";

@IsFilterable
export class LeadGroupBase implements MrfiktivLeadGroupViewModelGen, ICreateDto<ILeadGroup> {
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "id",
    config: {
      itemCallback: () => LeadGroupDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-lead-group",
      refType: BackendResourceEnum.LEAD_GROUP
    }
  })
  id: string;

  partnerId: string;

  configuration: IProjectConfiguration;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.leadGroup.title"
  })
  title: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.leadGroup.description"
  })
  description?: string | undefined;

  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  loading = false;

  constructor(leadGroup?: Partial<ILeadGroup | MrfiktivLeadGroupViewModelGen>) {
    this.id = leadGroup?.id ?? "";
    this.partnerId = leadGroup?.partnerId ?? "";
    this.configuration = new ProjectConfiguration(leadGroup?.configuration);
    this.title = leadGroup?.title ?? "";
    this.description = leadGroup?.description ?? "";
    this.timestamp = new Timestamp(leadGroup?.timestamp);
  }

  protected map(leadGroup?: MrfiktivLeadGroupViewModelGen) {
    if (!leadGroup) return;
    this.id = leadGroup?.id ?? "";
    this.partnerId = leadGroup?.partnerId ?? "";
    this.configuration = new ProjectConfiguration(leadGroup?.configuration);
    this.title = leadGroup?.title ?? "";
    this.description = leadGroup?.description ?? "";
    this.timestamp = new Timestamp(leadGroup?.timestamp);
  }

  private get customFieldConfigDto() {
    return this.configuration.customFieldConfig
      .filter((c) => c.customField.id)
      .map((c) => {
        return {
          configuration: c.configuration,
          customField: c.customField.id
        };
      });
  }

  async create() {
    const data: MrfiktivCreateLeadGroupDtoGen = {
      title: this.title,
      description: this.description || undefined,
      configuration: {
        isTemplate: this.configuration.isTemplate,
        customFieldConfig: this.customFieldConfigDto,
        views: []
      }
    };
    const res = await leadGroupService.create(this.partnerId, data);

    this.map(res);
    LeadGroupDataAccessLayer.set(this);

    return this;
  }

  async fetch(): Promise<this> {
    try {
      this.loading = true;
      const res = await leadGroupService.getOne(this.partnerId, this.id);

      this.map(res);
      LeadGroupDataAccessLayer.set(this);

      this.loading = false;

      return this;
    } catch (e) {
      Vue.$log.error(e);
      this.loading = false;
      throw e;
    }
  }

  async update() {
    const data: MrfiktivUpdateLeadGroupDtoGen = {
      title: this.title,
      description: this.description,
      configuration: {
        isTemplate: this.configuration.isTemplate,
        customFieldConfig: this.customFieldConfigDto,
        views: this.configuration.views.map((v) => v.dto)
      }
    };
    const res = await leadGroupService.update(this.partnerId, this.id, data);
    this.map(res);
    LeadGroupDataAccessLayer.set(this);

    return this;
  }

  async updatePartial(dto: MrfiktivUpdateLeadGroupDtoGen) {
    const res = await leadGroupService.update(this.partnerId, this.id, dto);

    this.map(res);

    LeadGroupDataAccessLayer.set(this);

    return this;
  }

  async delete() {
    const res = await leadGroupService.delete(this.partnerId, this.id);

    this.map(res);
    LeadGroupDataAccessLayer.delete(this);
  }
}

type ILeadGroup = LeadGroupBase;
const LeadGroup = Filter.createForClass(LeadGroupBase);

export { ILeadGroup, LeadGroup };
